// src/utils/request.ts
import axios from 'axios'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'

let redirectingToLogin = false

type RequestErrorWithFlags = Error & {
  handledByGlobalError?: boolean
}

const createSilentRequestError = (message: string) => {
  const error = new Error(message) as RequestErrorWithFlags
  error.handledByGlobalError = true
  return error
}

export const isGlobalHandledError = (error: unknown) => {
  return Boolean((error as RequestErrorWithFlags | undefined)?.handledByGlobalError)
}

const getBusinessMessage = (data: any) => {
  return data?.resultMessage ?? data?.ResultMessage ?? data?.resultMsg ?? data?.ResultMsg ?? data?.message ?? data?.Message
}

const parseBlobJson = async (data: unknown) => {
  if (!(data instanceof Blob)) return null

  const content = await data.slice(0, 4096).text()
  if (!content.trimStart().startsWith('{')) return null

  try {
    return JSON.parse(content)
  } catch {
    return null
  }
}

const redirectToLoginWithRedirect = () => {
  if (redirectingToLogin) return

  const path = window.location.pathname
  if (path === '/login') return

  redirectingToLogin = true
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')

  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}` || '/'
  const loginUrl = `/login?redirect=${encodeURIComponent(returnTo)}`
  window.location.replace(loginUrl)
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Object.prototype.toString.call(value) === '[object Object]'
}

const upperFirst = (key: string): string => {
  if (!key) return key
  return key.charAt(0).toUpperCase() + key.slice(1)
}

const toPascalFirstKeys = (input: unknown): unknown => {
  if (Array.isArray(input)) {
    return input.map((item) => toPascalFirstKeys(item))
  }

  if (!isPlainObject(input)) {
    return input
  }

  const result: Record<string, unknown> = {}
  Object.entries(input).forEach(([key, value]) => {
    result[upperFirst(key)] = toPascalFirstKeys(value)
  })
  return result
}

// 1. 创建 axios 实例（保持你原有的配置不变）
const service = axios.create({
  baseURL: '/api', // 基础路径
  timeout: 15000, // 超时时间
})

// 2. 请求拦截器：每次发请求前，自动带上 Token
service.interceptors.request.use(
  (config) => {
    // 优先从 localStorage 获取，也可以结合 Pinia 获取
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    config.headers['X-Client-Type'] = 'Web'

    // 与后端 PascalCase 约定对齐：统一将 query/body 的首字母转大写
    if (config.params && isPlainObject(config.params)) {
      config.params = toPascalFirstKeys(config.params)
    }

    if (config.data && isPlainObject(config.data)) {
      config.data = toPascalFirstKeys(config.data)
    }

    return config
  },
  (error) => Promise.reject(error)
)

// 3. 响应拦截器：统一处理错误与解包数据
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data
    const resultCode = res?.resultCode ?? res?.ResultCode
    const resultData = res?.resultData ?? res?.ResultData
    const resultMessage = getBusinessMessage(res)

    if (response.config.responseType === 'blob') {
      return res;
    }
    if (resultCode === undefined || resultCode === null) {
      return res;
    }
    
    // 假设后端返回 resultCode: 0 表示成功（根据你的后端实际字段调整）
    if (resultCode !== 0 && resultCode !== '0') {
      // 如果是 401 未授权，自动清除状态并跳转登录页
      if (resultCode === 401 || resultCode === '401') {
        const message = resultMessage || '登录已过期，请重新登录'
        ElMessage.warning(message)
        redirectToLoginWithRedirect()
        return Promise.reject(createSilentRequestError(message))
      }
      //已知业务报错
      if (resultCode === 20 || resultCode === '20') {
        return res
      }

      const message = resultMessage || '请求失败'
      ElMessage.error(message)
      
      return Promise.reject(createSilentRequestError(message))
    }
    
    // 直接返回业务数据（相当于自动解包了 response.data）
    return resultData
  },
  async (error) => {
    const status = Number(error?.response?.status)
    if (status === 401) {
      ElMessage.warning('登录已过期，请重新登录')
      redirectToLoginWithRedirect()
      error.handledByGlobalError = true
      return Promise.reject(error)
    }

    const responseData = error?.response?.data
    const blobResult = await parseBlobJson(responseData)
    const message = getBusinessMessage(blobResult || responseData) || error.message || '网络异常'
    ElMessage.error(message)
    error.handledByGlobalError = true
    return Promise.reject(error)
  }
)

// 4. 核心改动：封装支持泛型的快捷方法
const request = {
  get: <T = any>(url: string, params?: any, config?: AxiosRequestConfig) => {
    return service.get<any, T>(url, { params, ...config })
  },
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
    return service.post<any, T>(url, data, config)
  },
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
    return service.put<any, T>(url, data, config)
  },
  delete: <T = any>(url: string, params?: any, config?: AxiosRequestConfig) => {
    return service.delete<any, T>(url, { params, ...config })
  }
}

// 5. 导出
export default request